/**
 * DTOs for `GET /api/practice/today` (pedagogy spec §6.3.3, step 2.7b) and the slice of
 * `GET /api/flashcards/decks` the warm-up step reads. Mirrors `server/src/features/practice/
 * today.ts` and `todayNew.ts` by hand, like the rest of the client's DTOs.
 */
import type { PublicQuestion, QuestionType } from "@shared/types";
import type { PlanOffer } from "../types";

/** The three steps of the day's session, in the order they run. */
export const STEP_IDS = ["cards", "retest", "new"] as const;
export type StepId = (typeof STEP_IDS)[number];

export interface TodayRetestItem {
  questionId: string;
  topicId: string;
  type: QuestionType;
  /** The evidence key this item re-tests; sent back as `origin.retestOf`. */
  retestOf: string;
  lastExposedAt: number;
  reason: "miss" | "confirm" | "maintain";
  kind: "variant" | "same-topic";
  /** `include=questions` only. */
  question?: PublicQuestion;
}

/** Why the course calendar picked the topic (P1-3): prepare the next lecture, or catch up. */
export interface TodayNewTopicLecture {
  id: string;
  role: "prepare" | "catchUp";
}

export interface TodayNewTopic {
  topicId: string;
  chapter: number;
  path: "worked" | "practice";
  startedToday: boolean;
  /** The step's problems are all done today; no ids come with it. */
  doneToday: boolean;
  questionIds: string[];
  /** `include=questions` only, in `questionIds` order. */
  questions?: PublicQuestion[];
  sequenceId?: string;
  /** Set when the lecture calendar chose (or holds) the topic. */
  lecture?: TodayNewTopicLecture;
}

export interface TodayEvidence {
  topicsStarted: number;
  topicsTotal: number;
  solvedUnaided7d: number;
  retestsPassed7d: number;
  retestsTaken7d: number;
  /** Slice C, P1-7: problems worked on today and card reviews, never counted as solved. */
  practicedToday: number;
  cardsToday: number;
  cardReviews7d: number;
}

export interface TodayPlanDto {
  dayKey: string;
  examActive: boolean;
  retest: {
    items: TodayRetestItem[];
    candidates: number;
    skipped: number;
    oldestMissedDaysAgo: number | null;
  };
  /** `include=questions` only: plan-first offers for the re-tests that qualify. */
  plans?: Record<string, PlanOffer>;
  newTopic: TodayNewTopic | null;
  extras: { id: string; kind: string; href: string }[];
  evidence?: TodayEvidence;
}

/** `GET /api/flashcards/decks` → `mixed`: what the mixed review ("Dagens repetition") holds. */
export interface MixedCards {
  due: number;
  newToday: number;
  /** The moments the mixed queue draws from. */
  chapters: number[];
}
